import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { error } from 'components/common/helpers/toastify';
import Apiauth from '../../API/Apiauth.js';
import aibirdeyeIcon from '../../assets/images/aibirdeye.svg';
import aibirdeyeGrayIcon from '../../assets/images/aibirdeyegray.svg';
import collapseModeIcon from '../../assets/images/collapsemodeicon.svg';
import expandModeIcon from '../../assets/images/expandmodeicon.svg';
import { hasAiSuggestions, getUnmatchedProductIds, soldRefillItemsToChatProducts, buildAiSuggestionMessage, shouldShowAiSuggestionToast } from './zoneAiSuggestionsUtils';
import ZoneAiSuggestionsToast from './ZoneAiSuggestionsToast';
import ZoneChatWidget from './ZoneChatWidget';
import {
  filterLocationsByGridSearch,
  filterLocationsForZoneIndex,
  getLocationId,
  getLocationsWithZones,
  getSummarySoldProducts,
  getSummaryZoneRowKey,
  getZoneAiChatContext,
  getZoneOpenIssueId,
  parseZoneTotal,
  productHasSoldQty,
  zoneHasSoldProducts,
  zoneHasSummarySoldItems,
} from './zoneIndexGridUtils';

const MIN_AI_SUGGESTIONS_LOADER_MS = 1000;

const withMinLoaderDelay = async (promise, minMs = MIN_AI_SUGGESTIONS_LOADER_MS) => {
  const start = Date.now();
  const result = await promise;
  const elapsed = Date.now() - start;
  if (elapsed < minMs) {
    await new Promise((resolve) => setTimeout(resolve, minMs - elapsed));
  }
  return result;
};

const getSummaryProductId = (product) =>
  product?.po_product_id ?? product?.product_id ?? product?.id ?? null;

const isSummaryProductUnmatched = (product, unmatchedIds = []) => {
  const productId = getSummaryProductId(product);
  if (!productId) return false;
  return unmatchedIds.some((id) => String(id) === String(productId));
};

const getSummaryRefillProducts = (aiSuggestions) =>
  soldRefillItemsToChatProducts(aiSuggestions).filter(
    (product) => product?.main_image || product?.SKU
  );

const ZONE_IMAGE_PLACEHOLDER =
  'https://gis247.s3.us-east-2.amazonaws.com/placeholder-blank.png';

const handleZoneImageError = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = ZONE_IMAGE_PLACEHOLDER;
};

const getProductImageSrc = (product) =>
  product?.product_image || product?.main_image || ZONE_IMAGE_PLACEHOLDER;

const getProductSku = (product) =>
  product?.SKU || product?.sku || product?.Sku || '-';

const SummaryProductCell = ({ product, isUnmatched = false, isSkeleton = false }) => {
  const isSold = productHasSoldQty(product);

  return (
    <div
      className={`zone-index__summary-product-cell${
        isSold ? ' zone-index__summary-product-cell--sold' : ''
      }${isUnmatched ? ' zone-index__summary-product-cell--unmatched' : ''}${
        isSkeleton ? ' zone-index__summary-product-cell--skeleton' : ''
      }`}
    >
      <img
        src={getProductImageSrc(product)}
        alt={getProductSku(product)}
        loading="lazy"
        onError={handleZoneImageError}
      />
      {isSold ? (
        <span
          className="zone-index__summary-product-cell-overlay"
          aria-hidden
        />
      ) : null}
    </div>
  );
};

const getSummarySuggestProductKey = (product, index, zone) =>
  String(
    product?.id ||
      product?.SKU ||
      `${zone?.zone_record_id || zone?.zone_id || 'zone'}-refill-${index}`
  );

const SummarySuggestCheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path
      d="M2.5 6L5 8.5L9.5 3.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SummarySuggestCard = ({ product, isSelected, onSelect, t }) => (
  <button
    type="button"
    className={`zone-index__summary-suggest-card${
      isSelected ? ' zone-index__summary-suggest-card--selected' : ''
    }`}
    onClick={onSelect}
    aria-pressed={isSelected}
    aria-label={
      isSelected
        ? t('Deselect {{sku}}', {
            sku: product?.SKU || '-',
            defaultValue: 'Deselect {{sku}}',
          })
        : t('Select {{sku}}', {
            sku: product?.SKU || '-',
            defaultValue: 'Select {{sku}}',
          })
    }
  >
    <div className="zone-index__summary-suggest-card-image">
      <img
        src={product?.main_image || ZONE_IMAGE_PLACEHOLDER}
        alt=""
        loading="lazy"
        onError={handleZoneImageError}
      />
      <span className="zone-index__summary-suggest-card-tooltip">
        {t('Select this', { defaultValue: 'Select this' })}
      </span>
      <span className="zone-index__summary-suggest-card-select" aria-hidden>
        <SummarySuggestCheckIcon />
      </span>
    </div>
    <span className="zone-index__summary-suggest-card-sku">{product?.SKU || '-'}</span>
  </button>
);

const normalizeMultiZoneSuggestion = (zoneResult) => {
  const source = zoneResult?.data ?? zoneResult;

  return {
    issue_id: source?.issue_id ?? zoneResult?.issue_id,
    sold_refill: source?.sold_refill ?? zoneResult?.sold_refill,
    unmatched_replace: source?.unmatched_replace ?? zoneResult?.unmatched_replace,
  };
};

const findZoneInLocation = (location, zoneId) =>
  (location?.zones || []).find(
    (zone) =>
      String(zone?.zone_record_id ?? zone?.zone_id ?? '') === String(zoneId ?? '')
  );

export const ZoneIndexSummaryBulkAiButton = ({
  hasSelectedZones,
  isLoading,
  onClick,
  t,
}) => (
  <button
    type="button"
    className={`zone-index__icon-btn zone-index__summary-bulk-ai-btn${
      hasSelectedZones ? ' zone-index__summary-bulk-ai-btn--active' : ''
    }${isLoading ? ' zone-index__summary-bulk-ai-btn--loading' : ''}`}
    onClick={onClick}
    disabled={!hasSelectedZones || isLoading}
    aria-label={t('Get AI suggestions for selected zones', {
      defaultValue: 'Get AI suggestions for selected zones',
    })}
  >
    <span className="zone-index__summary-bulk-ai-icon-wrap" aria-hidden>
      <img
        src={aibirdeyeGrayIcon}
        alt=""
        className="zone-index__summary-bulk-ai-icon zone-index__summary-bulk-ai-icon--gray"
      />
      <img
        src={aibirdeyeIcon}
        alt=""
        className="zone-index__summary-bulk-ai-icon zone-index__summary-bulk-ai-icon--color"
      />
    </span>
  </button>
);

export const ZoneIndexSummarySelectionBar = ({
  selectedCount,
  isAllSelected,
  onSelectAllChange,
  onClear,
  t,
}) => (
  <div className="zone-index__summary-selection-bar">
    <p className="zone-index__summary-selection-count">
      <span className="zone-index__summary-selection-count-value">{selectedCount}</span>
      <span className="zone-index__summary-selection-count-label">{t('Selected')}</span>
    </p>
    <label className="zone-index__summary-selection-select-all">
      <input
        type="checkbox"
        className="zone-index__summary-checkbox"
        checked={isAllSelected}
        onChange={(event) => onSelectAllChange?.(event.target.checked)}
      />
      <span className="zone-index__summary-selection-select-all-label">{t('Select All')}</span>
    </label>
    <button
      type="button"
      className="zone-index__summary-selection-clear"
      onClick={onClear}
    >
      <svg
        className="zone-index__summary-selection-clear-icon"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden
      >
        <path
          d="M5 5L15 15M15 5L5 15"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
        />
      </svg>
      <span className="zone-index__summary-selection-clear-label">{t('Clear')}</span>
    </button>
  </div>
);

const SummarySuggestLoading = ({ t }) => (
  <div className="zone-index__summary-suggest-loading" role="status" aria-live="polite">
    <span className="zone-index__summary-suggest-loading-tooltip">
      {t('Wait for AI to process', { defaultValue: 'Wait for AI to process' })}
    </span>
    <span className="zone-index__summary-suggest-loading-spinner" aria-hidden />
    <span className="zone-index__summary-suggest-loading-text">
      {t('Processing…', { defaultValue: 'Processing…' })}
    </span>
  </div>
);

const SummarySoldCard = ({ product, isSkeleton = false }) => (
  <div
    className={`zone-index__summary-sold-card${
      isSkeleton ? ' zone-index__summary-sold-card--skeleton' : ''
    }`}
  >
    <div className="zone-index__summary-sold-card-image">
      <img
        src={getProductImageSrc(product)}
        alt={getProductSku(product)}
        loading="lazy"
        onError={handleZoneImageError}
      />
    </div>
    <span className="zone-index__summary-sold-card-sku">{getProductSku(product)}</span>
  </div>
);

const SummaryZoneRow = ({
  zone,
  location,
  t,
  isAiLoading,
  zoneSuggestions,
  onAiSuggestClick,
  isZoneSelected,
  onZoneCheckChange,
  disableIssueNavigation = false,
}) => {
  const navigate = useNavigate();
  const products = zone?.products || [];
  const soldProducts = getSummarySoldProducts(zone);
  const refillProducts = zoneSuggestions ? getSummaryRefillProducts(zoneSuggestions) : [];
  const unmatchedProductIds = zoneSuggestions ? getUnmatchedProductIds(zoneSuggestions) : [];
  const { current, max } = parseZoneTotal(zone?.total);
  const hasSoldAlert = zoneHasSoldProducts(zone);
  const hasUnrefilledSold = zoneHasSummarySoldItems(zone);
  const openIssueId = getZoneOpenIssueId(zone);
  const canOpenIssue = Boolean(openIssueId) && !disableIssueNavigation;
  const [selectedSuggestKey, setSelectedSuggestKey] = useState(null);

  useEffect(() => {
    setSelectedSuggestKey(null);
  }, [zoneSuggestions]);

  const handleSuggestSelect = useCallback((productKey) => {
    setSelectedSuggestKey((current) => (current === productKey ? null : productKey));
  }, []);

  const openIssuePage = () => {
    if (!canOpenIssue) return;
    navigate(`/app/inventory/zone/edit/${openIssueId}`);
  };

  return (
    <div className="zone-index__summary-zone-row">
      <div className="zone-index__summary-row-check">
        <input
          type="checkbox"
          className="zone-index__summary-checkbox"
          checked={isZoneSelected}
          onChange={(event) => onZoneCheckChange(event.target.checked)}
          aria-label={t('Select {{zone}}', {
            zone: zone?.zone || '-',
            defaultValue: 'Select {{zone}}',
          })}
        />
      </div>

      <div className="zone-index__summary-zone-header">
        <span className="zone-index__summary-zone-icon">
          {zone?.zone_logo ? (
            <img
              src={zone.zone_logo}
              alt=""
              aria-hidden
              onError={handleZoneImageError}
            />
          ) : (
            <span className="zone-index__summary-zone-icon-fallback" aria-hidden />
          )}
        </span>
        <span className="zone-index__summary-zone-name">{zone?.zone || '-'}</span>
        <span className="zone-index__summary-zone-total">
          (
          <span
            className={`zone-index__summary-zone-total-current${
              hasUnrefilledSold
                ? ' zone-index__summary-zone-total-current--alert'
                : ''
            }`}
          >
            {current}
          </span>
          /{max})
        </span>
        {hasSoldAlert ? (
          <span className="zone-index__summary-zone-alert-dot" aria-hidden />
        ) : null}
      </div>

      <div
        className={`zone-index__summary-zone-grid${
          canOpenIssue ? ' zone-index__summary-zone-grid--clickable' : ''
        }${isAiLoading ? ' zone-index__summary-zone-grid--loading' : ''}`}
        onClick={openIssuePage}
        onKeyDown={(event) => {
          if (!canOpenIssue) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openIssuePage();
          }
        }}
        role={canOpenIssue ? 'button' : undefined}
        tabIndex={canOpenIssue ? 0 : undefined}
      >
        {products.length ? (
          products.map((product, index) => (
            <SummaryProductCell
              key={
                product?.po_product_id ||
                product?.product_id ||
                product?.id ||
                `${zone?.zone_record_id || zone?.zone_id}-${index}`
              }
              product={product}
              isUnmatched={isSummaryProductUnmatched(product, unmatchedProductIds)}
              isSkeleton={isAiLoading}
            />
          ))
        ) : (
          <div className="zone-index__summary-zone-grid-empty">{t('No products')}</div>
        )}
      </div>

      <div className="zone-index__summary-sold-col">
        <div className="zone-index__summary-sold-cards">
          {soldProducts.map((product, index) => (
            <SummarySoldCard
              key={
                product?.po_product_id ||
                product?.product_id ||
                product?.id ||
                `${zone?.zone_record_id || zone?.zone_id}-sold-${index}`
              }
              product={product}
              isSkeleton={isAiLoading}
            />
          ))}
        </div>
      </div>

      <div
        className={`zone-index__summary-suggest-col${
          isAiLoading || (!refillProducts.length && !zoneSuggestions)
            ? ' zone-index__summary-suggest-col--centered'
            : ''
        }`}
      >
        <div className="zone-index__summary-suggest-body">
          {isAiLoading ? (
            <SummarySuggestLoading t={t} />
          ) : refillProducts.length ? (
            <div className="zone-index__summary-suggest-cards">
              {refillProducts.map((product, index) => {
                const productKey = getSummarySuggestProductKey(product, index, zone);

                return (
                <SummarySuggestCard
                  key={productKey}
                  product={product}
                  isSelected={selectedSuggestKey === productKey}
                  onSelect={() => handleSuggestSelect(productKey)}
                  t={t}
                />
                );
              })}
            </div>
          ) : zoneSuggestions ? null : (
            <button
              type="button"
              className="zone-index__summary-suggest-btn"
              onClick={() => onAiSuggestClick(zone, location)}
            >
              <span className="zone-index__summary-suggest-btn-icon-wrap" aria-hidden>
                <img
                  src={aibirdeyeGrayIcon}
                  alt=""
                  className="zone-index__summary-suggest-btn-icon zone-index__summary-suggest-btn-icon--gray"
                />
                <img
                  src={aibirdeyeIcon}
                  alt=""
                  className="zone-index__summary-suggest-btn-icon zone-index__summary-suggest-btn-icon--color"
                />
              </span>
              <span>{t('AI')}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const ZoneIndexSummaryCollapseToggle = ({
  isExpanded,
  onToggle,
  t,
}) => (
  <button
    type="button"
    className="zone-index__icon-btn zone-index__summary-mode-toggle"
    onClick={onToggle}
    aria-label={isExpanded ? t('Collapse all') : t('Expand all')}
    aria-expanded={isExpanded}
  >
    <img
      src={isExpanded ? collapseModeIcon : expandModeIcon}
      alt=""
      aria-hidden
    />
  </button>
);

const SummaryLocationSection = ({
  location,
  t,
  loadingAiZoneKeys,
  zoneSuggestionsByKey,
  onAiSuggestClick,
  selectedZoneKeys,
  onSelectedZoneKeysChange,
  allLocationsExpanded,
  collapseResetKey,
  disableIssueNavigation = false,
}) => {
  const [isLocallyCollapsed, setIsLocallyCollapsed] = useState(false);

  const zoneRowKeys = useMemo(
    () =>
      (location?.zones || []).map((zone) => getSummaryZoneRowKey(zone, location)),
    [location]
  );

  useEffect(() => {
    setIsLocallyCollapsed(false);
  }, [collapseResetKey]);

  const isLocationSelected =
    zoneRowKeys.length > 0 &&
    zoneRowKeys.every((rowKey) => selectedZoneKeys.has(rowKey));

  const handleLocationCheckChange = (event) => {
    const { checked } = event.target;
    onSelectedZoneKeysChange?.(checked ? new Set(zoneRowKeys) : new Set());
  };

  const handleZoneCheckChange = useCallback(
    (rowKey, checked) => {
      onSelectedZoneKeysChange?.(
        (() => {
          const next = new Set(selectedZoneKeys);
          if (checked) {
            next.add(rowKey);
          } else {
            next.delete(rowKey);
          }
          return next;
        })()
      );
    },
    [onSelectedZoneKeysChange, selectedZoneKeys]
  );

  const isCollapsed = !allLocationsExpanded || isLocallyCollapsed;

  return (
    <section className="zone-index__summary-location">
      <header className="zone-index__summary-location-header">
        <div className="zone-index__summary-location-title-row">
          <input
            type="checkbox"
            className="zone-index__summary-checkbox"
            checked={isLocationSelected}
            onChange={handleLocationCheckChange}
            aria-label={t('Select location')}
          />
          <button
            type="button"
            className={`zone-index__summary-collapse-btn${
              isCollapsed ? ' zone-index__summary-collapse-btn--collapsed' : ''
            }`}
            onClick={() => setIsLocallyCollapsed((value) => !value)}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? t('Expand') : t('Collapse')}
          >
            <span className="zone-index__summary-collapse-icon" aria-hidden />
          </button>
          <div className="zone-index__summary-location-title">
            <span className="zone-index__summary-location-name">
              {location?.name || '-'}
            </span>
            <span className="zone-index__summary-location-count">
              ({location.zones.length} {t('Zones')})
            </span>
          </div>
        </div>

        {!isCollapsed ? (
          <div className="zone-index__summary-column-headers">
            <span className="zone-index__summary-column-header zone-index__summary-column-header--zone">
              {t('Zone')}
            </span>
            <span className="zone-index__summary-column-header zone-index__summary-column-header--sold">
              {t('Sold items')}
            </span>
            <span className="zone-index__summary-column-header zone-index__summary-column-header--suggest">
              {t('Suggestion')}
            </span>
          </div>
        ) : null}
      </header>

      {!isCollapsed
        ? location.zones.map((zone) => {
            const rowKey = getSummaryZoneRowKey(zone, location);

            return (
            <SummaryZoneRow
              key={zone.zone_record_id || `${location.location_id}-${zone.zone_id}`}
              zone={zone}
              location={location}
              t={t}
              isAiLoading={loadingAiZoneKeys.has(rowKey)}
              zoneSuggestions={zoneSuggestionsByKey[rowKey] ?? null}
              onAiSuggestClick={onAiSuggestClick}
              isZoneSelected={selectedZoneKeys.has(rowKey)}
              onZoneCheckChange={(checked) => handleZoneCheckChange(rowKey, checked)}
              disableIssueNavigation={disableIssueNavigation}
            />
            );
          })
        : null}
    </section>
  );
};

const findZoneAcrossLocations = (locations, zoneId, locationId) => {
  const location = (locations || []).find(
    (item) => String(getLocationId(item)) === String(locationId ?? '')
  );
  if (!location) return null;
  return findZoneInLocation(location, zoneId);
};

export default function ZoneIndexSummaryView({
  locations = [],
  appliedFilters,
  searchQuery = '',
  allLocationsExpanded = true,
  collapseResetKey = 0,
  onBulkAiToolbarChange,
  onSelectionToolbarChange,
  t,
  disableIssueNavigation = false,
}) {
  const filteredLocations = filterLocationsForZoneIndex(locations, appliedFilters);
  const locationsWithZones = getLocationsWithZones(filteredLocations);
  const visibleLocations = filterLocationsByGridSearch(
    locationsWithZones,
    searchQuery
  );
  const [loadingAiZoneKeys, setLoadingAiZoneKeys] = useState(() => new Set());
  const [zoneSuggestionsByKey, setZoneSuggestionsByKey] = useState({});
  const [aiSuggestionEntries, setAiSuggestionEntries] = useState([]);
  const [aiSuggestionToastsByKey, setAiSuggestionToastsByKey] = useState({});
  const [chatContext, setChatContext] = useState(null);
  const [selectedZoneKeysByLocation, setSelectedZoneKeysByLocation] = useState({});

  useEffect(() => {
    setSelectedZoneKeysByLocation({});
  }, [collapseResetKey]);

  const handleLocationSelectionChange = useCallback((locationId, selectedKeys) => {
    setSelectedZoneKeysByLocation((prev) => {
      if (!selectedKeys?.size) {
        if (!prev[locationId]) return prev;
        const next = { ...prev };
        delete next[locationId];
        return next;
      }
      return { ...prev, [locationId]: selectedKeys };
    });
  }, []);

  const selectedSummaryRowKeys = useMemo(() => {
    const keys = new Set();
    Object.values(selectedZoneKeysByLocation).forEach((locationKeys) => {
      locationKeys.forEach((rowKey) => keys.add(rowKey));
    });
    return keys;
  }, [selectedZoneKeysByLocation]);

  const hasSelectedZones = selectedSummaryRowKeys.size > 0;

  const visibleZoneRowKeys = useMemo(() => {
    const keys = [];
    visibleLocations.forEach((location) => {
      (location?.zones || []).forEach((zone) => {
        keys.push(getSummaryZoneRowKey(zone, location));
      });
    });
    return keys;
  }, [visibleLocations]);

  const isAllZonesSelected =
    visibleZoneRowKeys.length > 0 &&
    selectedSummaryRowKeys.size === visibleZoneRowKeys.length;

  const handleSelectAllZones = useCallback(() => {
    const next = {};
    visibleLocations.forEach((location) => {
      const locationId = getLocationId(location);
      next[locationId] = new Set(
        (location?.zones || []).map((zone) => getSummaryZoneRowKey(zone, location))
      );
    });
    setSelectedZoneKeysByLocation(next);
  }, [visibleLocations]);

  const handleClearAllZones = useCallback(() => {
    setSelectedZoneKeysByLocation({});
  }, []);

  const handleSelectAllChange = useCallback(
    (checked) => {
      if (checked) {
        handleSelectAllZones();
        return;
      }
      handleClearAllZones();
    },
    [handleClearAllZones, handleSelectAllZones]
  );

  const isBulkAiLoading = loadingAiZoneKeys.size > 0;

  const activeAiSuggestionToasts = useMemo(
    () =>
      Object.entries(aiSuggestionToastsByKey)
        .filter(([, toast]) => toast?.message && !toast?.dismissed)
        .map(([rowKey, toast]) => ({ rowKey, message: toast.message })),
    [aiSuggestionToastsByKey]
  );

  const hasChatNotification = activeAiSuggestionToasts.length > 0;

  const applySuggestionsForZone = useCallback(
    ({ rowKey, suggestionsData, zoneLabel, context }) => {
      setZoneSuggestionsByKey((prev) => ({
        ...prev,
        [rowKey]: suggestionsData,
      }));

      if (!hasAiSuggestions(suggestionsData)) return;

      const toastMessage = buildAiSuggestionMessage(suggestionsData, zoneLabel, t);

      setAiSuggestionEntries((prev) => {
        const filtered = prev.filter((entry) => entry.id !== rowKey);
        return [
          ...filtered,
          {
            id: rowKey,
            zoneLabel,
            data: suggestionsData,
            context,
          },
        ];
      });

      if (toastMessage && shouldShowAiSuggestionToast(suggestionsData)) {
        setAiSuggestionToastsByKey((prev) => ({
          ...prev,
          [rowKey]: {
            message: toastMessage,
            dismissed: false,
          },
        }));
      }
    },
    [t]
  );

  const applyBulkSuggestionsForZones = useCallback(
    (zoneResults = []) => {
      if (!zoneResults.length) return;

      const nextSuggestionsByKey = {};
      const nextEntries = [];
      const nextToasts = {};

      zoneResults.forEach(({ rowKey, suggestionsData, zoneLabel, context }) => {
        nextSuggestionsByKey[rowKey] = suggestionsData;

        if (!hasAiSuggestions(suggestionsData)) return;

        nextEntries.push({
          id: rowKey,
          zoneLabel,
          data: suggestionsData,
          context,
        });

        const toastMessage = buildAiSuggestionMessage(suggestionsData, zoneLabel, t);
        if (toastMessage && shouldShowAiSuggestionToast(suggestionsData)) {
          nextToasts[rowKey] = {
            message: toastMessage,
            dismissed: false,
          };
        }
      });

      setZoneSuggestionsByKey((prev) => ({
        ...prev,
        ...nextSuggestionsByKey,
      }));

      setAiSuggestionEntries(nextEntries);
      setAiSuggestionToastsByKey(nextToasts);
    },
    [t]
  );

  const handleAiSuggestClick = useCallback(
    async (zone, location) => {
      const rowKey = getSummaryZoneRowKey(zone, location);
      if (loadingAiZoneKeys.has(rowKey)) return;

      const context = getZoneAiChatContext(zone, location);
      if (!context) {
        error(
          t('Unable to load refill suggestions. Issue context is missing.', {
            defaultValue: 'Unable to load refill suggestions. Issue context is missing.',
          })
        );
        return;
      }

      setChatContext({
        ...context,
        zoneLabel: zone?.zone || '',
      });
      setAiSuggestionEntries([]);
      setAiSuggestionToastsByKey({});
      setLoadingAiZoneKeys(new Set([rowKey]));

      try {
        const suggestionsResponse = await withMinLoaderDelay(
          Apiauth(
            {
              zone_id: context.zoneId,
              location_id: context.locationId,
              issue_id: context.issueId,
            },
            'zone/ai-suggestions'
          )
        );

        if (suggestionsResponse?.code !== 200) {
          error(
            t('Unable to load refill suggestions. Please try again.', {
              defaultValue: 'Unable to load refill suggestions. Please try again.',
            })
          );
          return;
        }

        applySuggestionsForZone({
          rowKey,
          suggestionsData: suggestionsResponse.data || null,
          zoneLabel: zone?.zone || '',
          context,
        });
      } catch (err) {
        console.error(err, 'AI suggestions API Error');
        error(
          t('Unable to load refill suggestions. Please try again.', {
            defaultValue: 'Unable to load refill suggestions. Please try again.',
          })
        );
      } finally {
        setLoadingAiZoneKeys(new Set());
      }
    },
    [applySuggestionsForZone, loadingAiZoneKeys, t]
  );

  const handleBulkAiSuggestClick = useCallback(async () => {
    if (loadingAiZoneKeys.size > 0 || !hasSelectedZones) return;

    const selectedRequests = [];

    visibleLocations.forEach((location) => {
      const locationId = getLocationId(location);
      const selectedKeys = selectedZoneKeysByLocation[locationId];
      if (!selectedKeys?.size) return;

      (location?.zones || []).forEach((zone) => {
        const rowKey = getSummaryZoneRowKey(zone, location);
        if (!selectedKeys.has(rowKey)) return;

        const context = getZoneAiChatContext(zone, location);
        if (!context) return;

        selectedRequests.push({
          rowKey,
          zone,
          location,
          context,
          payload: {
            zone_id: context.zoneId,
            location_id: context.locationId,
            issue_id: context.issueId,
          },
        });
      });
    });

    if (!selectedRequests.length) {
      error(
        t('Unable to load refill suggestions. Issue context is missing.', {
          defaultValue: 'Unable to load refill suggestions. Issue context is missing.',
        })
      );
      return;
    }

    setLoadingAiZoneKeys(new Set(selectedRequests.map((item) => item.rowKey)));
    setAiSuggestionEntries([]);
    setAiSuggestionToastsByKey({});

    const firstRequest = selectedRequests[0];
    setChatContext({
      ...firstRequest.context,
      zoneLabel: firstRequest.zone?.zone || '',
    });

    try {
      const response = await withMinLoaderDelay(
        Apiauth(
          { zones: selectedRequests.map((item) => item.payload) },
          'zone/ai-suggestions-multi'
        )
      );

      if (response?.code !== 200) {
        error(
          t('Unable to load refill suggestions. Please try again.', {
            defaultValue: 'Unable to load refill suggestions. Please try again.',
          })
        );
        return;
      }

      const bulkZoneResults = [];

      (response.data?.zones || []).forEach((zoneResult) => {
        const zone = findZoneAcrossLocations(
          visibleLocations,
          zoneResult?.zone_id,
          zoneResult?.location_id
        );
        if (!zone) return;

        const location = visibleLocations.find(
          (item) =>
            String(getLocationId(item)) === String(zoneResult?.location_id ?? '')
        );
        if (!location) return;

        const rowKey = getSummaryZoneRowKey(zone, location);
        const context = getZoneAiChatContext(zone, location);
        if (!context) return;

        bulkZoneResults.push({
          rowKey,
          suggestionsData: normalizeMultiZoneSuggestion(zoneResult),
          zoneLabel: zone?.zone || '',
          context,
        });
      });

      applyBulkSuggestionsForZones(bulkZoneResults);
    } catch (err) {
      console.error(err, 'AI suggestions multi API Error');
      error(
        t('Unable to load refill suggestions. Please try again.', {
          defaultValue: 'Unable to load refill suggestions. Please try again.',
        })
      );
    } finally {
      setLoadingAiZoneKeys(new Set());
    }
  }, [
    applyBulkSuggestionsForZones,
    hasSelectedZones,
    loadingAiZoneKeys,
    selectedZoneKeysByLocation,
    t,
    visibleLocations,
  ]);

  useEffect(() => {
    onBulkAiToolbarChange?.({
      hasSelectedZones,
      isLoading: isBulkAiLoading,
      onClick: handleBulkAiSuggestClick,
    });

    return () => {
      onBulkAiToolbarChange?.(null);
    };
  }, [
    handleBulkAiSuggestClick,
    hasSelectedZones,
    isBulkAiLoading,
    onBulkAiToolbarChange,
  ]);

  useEffect(() => {
    onSelectionToolbarChange?.({
      hasSelectedZones,
      selectedCount: selectedSummaryRowKeys.size,
      isAllSelected: isAllZonesSelected,
      onSelectAllChange: handleSelectAllChange,
      onClear: handleClearAllZones,
    });

    return () => {
      onSelectionToolbarChange?.(null);
    };
  }, [
    handleClearAllZones,
    handleSelectAllChange,
    hasSelectedZones,
    isAllZonesSelected,
    onSelectionToolbarChange,
    selectedSummaryRowKeys.size,
  ]);

  if (!visibleLocations.length) {
    return (
      <div className="zone-index__empty zone-index__empty--grid">
        {t('No Data Available In Table')}
      </div>
    );
  }

  return (
    <>
      <div className="zone-index__summary-view">
        {visibleLocations.map((location) => {
          const locationId = getLocationId(location);

          return (
          <SummaryLocationSection
            key={location.location_id}
            location={location}
            t={t}
            loadingAiZoneKeys={loadingAiZoneKeys}
            zoneSuggestionsByKey={zoneSuggestionsByKey}
            onAiSuggestClick={handleAiSuggestClick}
            selectedZoneKeys={selectedZoneKeysByLocation[locationId] || new Set()}
            onSelectedZoneKeysChange={(selectedKeys) =>
              handleLocationSelectionChange(locationId, selectedKeys)
            }
            allLocationsExpanded={allLocationsExpanded}
            collapseResetKey={collapseResetKey}
            disableIssueNavigation={disableIssueNavigation}
          />
          );
        })}
      </div>

      {activeAiSuggestionToasts.map((toast, index) => (
        <ZoneAiSuggestionsToast
          key={`ai-suggestions-${toast.rowKey}`}
          message={toast.message}
          stackIndex={index}
          onClose={() =>
            setAiSuggestionToastsByKey((prev) => ({
              ...prev,
              [toast.rowKey]: {
                ...prev[toast.rowKey],
                dismissed: true,
              },
            }))
          }
        />
      ))}

      <ZoneChatWidget
        appendAiSuggestionsEntries={aiSuggestionEntries}
        isEditMode={false}
        isChatEnabled
        zoneId={chatContext?.zoneId}
        locationId={chatContext?.locationId}
        issueId={chatContext?.issueId}
        zoneLabel={chatContext?.zoneLabel}
        hasNotification={hasChatNotification}
        hideComposerAndPresets
        disableChatSessionControls
        panelTitle={t('AI Suggestions', { defaultValue: 'AI Suggestions' })}
      />
    </>
  );
}
